import {
    OptionDto,
    LanguageDto,
    OptionTextDto,
    OptionBasicTextDto,
    ModeltypeBasicDto,
    AssignedModeltypeBasicDto,
    AssignedModeltypeBasicDtoAssignmentType,
    OptionBasicDto,
    WorkflowDto,
    WorkflowStatusDtoType,
    OptionDtoTextExportStatus,
    CnrDto,
    OptionDtoSpecificType,
    OptionBasicCoreDto, FlagDto, OptionTextPCCDExportDto,
} from "../../services/Client";
import {TDerivateStates, EOptionState} from "../../components/DerivateOptionBar";
import * as _ from "lodash";
import {IRowStatusProps, getRowStatusProps, getDtoStatus, valueState2str} from "../../services/WorkflowUtils";
import {IMultiLineValue, lang2flag} from "../../components/MultiLineColumn";
import {EditableValue} from "../../components/BaseColumn";
import ColumnFactory from "../../services/ColumnFactory";
import {PropertiesKey} from "./OptionsViewUtils";
import {DateFormatter} from "../../utils/DateFormatter";
import {IOptionIndicator} from "../../components/Table/VirtualizedTableInterfaces";
import {DerivateUtils} from "../../components/Table/components/derivate/DerivateUtils";

export interface DataAdapterOptionsViewDerivates {
    [objectId: number]: string;
}

interface IDataAdapterOptionsViewState {
    derivateStates?: TDerivateStates;
}
/** adapter for data to handle other presentation of some data */
export default class DataAdapterOptionsView {
    /**
     * creates list of unique derivate names
     * @param derivates list of derivates
     */
    public static createDerivatesList(derivates: ModeltypeBasicDto[], userLanguage: string): Map<number, string> {
        let result: Map<number, string> = new Map<number, string>();
        derivates.forEach(dto => result.set(dto.objectId, DataAdapterOptionsView.createDerivateName(dto, userLanguage)));
        return result;
    }

    /**
     * creates uniq derivate name
     * @param derivate derivate for which to create name
     */
    public static createDerivateName(derivate: ModeltypeBasicDto, userLanguage?: string): string {
        let derivateName;

        if (userLanguage) {
            derivateName = DerivateUtils.renderDerivateName(derivate, userLanguage);
        }

        const name = (derivateName ? derivateName : '<derivate>').concat(
            ' (',
            derivate.btyp ? derivate.btyp : derivate.objectId.toString(),
            ')'
        );
        return name;
    }

    /**
     * creates mapping from object id to unique derivate name
     * @param derivates list of derivates
     * @param typefamily typefamily to filter for
     */
    public static createIdToDerivateMap(derivates: ModeltypeBasicDto[], userLanguage: string): DataAdapterOptionsViewDerivates {
        const result: DataAdapterOptionsViewDerivates = {};
        _.forEach(derivates, dto => result[dto.objectId] = DataAdapterOptionsView.createDerivateName(dto, userLanguage));
        return result;
    }

    /**
     * converts DB value of assignment to EOptionState
     * @param assignmentType DB value of assignment
     * @returns EOptionState
     */
    public static getOptionState(assignmentType: string): EOptionState {
        // O for optional, S for standard, W for wahlweise
        // OE for optional draft, SE for standard draft, WE for wahlweise draft
        // and empty for not assigned
        switch (assignmentType.toUpperCase()) {
            case AssignedModeltypeBasicDtoAssignmentType.O:
                return EOptionState.Optional;
            case AssignedModeltypeBasicDtoAssignmentType.S:
                return EOptionState.Standard;
            case AssignedModeltypeBasicDtoAssignmentType.W:
                return EOptionState.Available;
            case AssignedModeltypeBasicDtoAssignmentType.OE:
                return EOptionState.OptionalDraft;
            case AssignedModeltypeBasicDtoAssignmentType.SE:
                return EOptionState.StandardDraft;
            case AssignedModeltypeBasicDtoAssignmentType.WE:
                return EOptionState.AvailableDraft;
            case AssignedModeltypeBasicDtoAssignmentType.OF:
                return EOptionState.OptionalF;
            case AssignedModeltypeBasicDtoAssignmentType.SS:
                return EOptionState.StandardS;
            case AssignedModeltypeBasicDtoAssignmentType.SC:
                return EOptionState.StandardC;
            case AssignedModeltypeBasicDtoAssignmentType.SG:
                return EOptionState.StandardG;
            case AssignedModeltypeBasicDtoAssignmentType.SV:
                return EOptionState.StandardV;
            case AssignedModeltypeBasicDtoAssignmentType.SB:
                return EOptionState.StandardB;
            default:
                return EOptionState.NotAvailable;
        }
    }

    /**
     * converts EOptionState to DB value of assignment
     * @param optionState option state value
     * @returns DB value of assignment
     */
    public static getAssigment(optionState: EOptionState): AssignedModeltypeBasicDtoAssignmentType {
        // O for optional, S for standard, W for wahlweise
        // OE for optional draft, SE for standard draft, WE for wahlweise draft
        // and empty for not assigned
        switch (optionState) {
            case EOptionState.Optional:
                return AssignedModeltypeBasicDtoAssignmentType.O;
            case EOptionState.Standard:
                return AssignedModeltypeBasicDtoAssignmentType.S;
            case EOptionState.Available:
                return AssignedModeltypeBasicDtoAssignmentType.W;
            case EOptionState.OptionalDraft:
                return AssignedModeltypeBasicDtoAssignmentType.OE;
            case EOptionState.StandardDraft:
                return AssignedModeltypeBasicDtoAssignmentType.SE;
            case EOptionState.AvailableDraft:
                return AssignedModeltypeBasicDtoAssignmentType.WE;
            case EOptionState.StandardV:
                return AssignedModeltypeBasicDtoAssignmentType.SV;
            case EOptionState.StandardC:
                return AssignedModeltypeBasicDtoAssignmentType.SC;
            case EOptionState.StandardG:
                return AssignedModeltypeBasicDtoAssignmentType.SG;
            case EOptionState.StandardS:
                return AssignedModeltypeBasicDtoAssignmentType.SS;
            case EOptionState.StandardB:
                return AssignedModeltypeBasicDtoAssignmentType.SB;
            case EOptionState.OptionalF:
                return AssignedModeltypeBasicDtoAssignmentType.OF;
            default:
                return AssignedModeltypeBasicDtoAssignmentType.N;
        }
    }

    private state: IDataAdapterOptionsViewState = {};
    private optionStatus: IRowStatusProps;

    constructor(
        public readonly dto: OptionDto,
        public readonly derivates: ModeltypeBasicDto[],
        public readonly derivatesMap: DataAdapterOptionsViewDerivates,
        public readonly workflows: WorkflowDto[],
        public languages: LanguageDto[],
        public readonly cnrs: CnrDto[]
    ) {
        this.getText = this.getText.bind(this);
        this.setText = this.setText.bind(this);
        this.setOptionTextPCCDExports = this.setOptionTextPCCDExports.bind(this);
        this.getOptionTextPCCDExports = this.getOptionTextPCCDExports.bind(this);
        this.updateDerivateStates = this.updateDerivateStates.bind(this);

        this.optionStatus = getRowStatusProps(dto.status, dto.rejectedStates ? dto.rejectedStates : [], workflows);
    }

    public setSelectedLanguages(languages: LanguageDto[]) {
        this.languages = languages;
    }

    // getter
    get value() {
        return this.dto;
    }

    get pccdFlag() {
        return this.dto.pccdFlag;
    }

    get parentOptionId() {
        return this.dto.parentId;
    }

    get specificType() {
        return this.dto.specificType;
    }

    get cnr() {
        if (this.cnrs.length > 0) {
            return _.find(this.cnrs, (cnr: CnrDto) => cnr.objectId === this.dto.cnrId);
        }
        else {
            return undefined;
        }
    }

    get objectId() {
        return this.dto.objectId;
    }

    get intSequence() {
        return this.dto.intSequence;
    }

    get availableEnd() {
        if (!this.dto.availableEnd) {
            return undefined;
        }
        return DateFormatter.applyDateFormat(new Date(this.dto.availableEnd));
    }

    get availableStart() {
        if (!this.dto.availableStart) {
            return undefined;
        }
        return DateFormatter.applyDateFormat(new Date(this.dto.availableStart));
    }

    get categoryText() {
        return this.dto.optionBasic.categoryText;
    }

    get comment() {
        return this.dto.comment;
    }

    get constraint() {
        return this.dto.constraint;
    }

    get exclusion() {
        return this.dto.exclusion;
    }

    get packageContent() {
        return this.dto.packageContent;
    }

    get flags() {
        return this.dto.flags;
    }

    get derivateStates(): TDerivateStates {
        this.updateDerivateStates();
        return this.state.derivateStates ? this.state.derivateStates : {};
    }

    get optionBasic(): OptionBasicDto {
        return this.dto.optionBasic;
    }

    get price(): number | undefined {
        return this.dto.price;
    }
    get pdiptext(): string | undefined {
        return this.dto.optionBasic.pdiptext;
    }
    /** Accessor for readonly option basic column in OptionsView. */

    get optionBasicView(): string {
        return ColumnFactory.optionBasic2str(this.optionBasic);
    }

    get texts(): OptionTextDto[] {
        return this.dto.texts ? this.dto.texts : [];
    }

    get statusView() {
        return this.optionStatus.statusView;
    }

    get statusValueState() {
        return this.optionStatus.statusValueState;
    }

    get statusResetRoles() {
        return this.optionStatus.resetRoles;
    }

    get workFlowName() {
        return this.optionStatus.workFlowName;
    }

    get workflowType() {
        return this.optionStatus.workflowType;
    }

    get workflowResetPointRejection() {
        return this.optionStatus.workflowResetPointRejection;
    }

    get statusValueStateEditable() {
        if (this.optionStatus.statusView === WorkflowStatusDtoType.FREEZE) {
            // internal texts are always readonly when in FREEZE state
            return EditableValue.Readonly;
        }
        return this.optionStatus.statusValueStateEditable;
    }

    get statusValueStateResetable() {
        if (_.includes(this.value.status, "_RESET")) {
            return true;
        }
        return false;
    }

    get statusValueStates() {
        return this.optionStatus.statusValueStates;
    }

    get optionTypeView(): string | undefined {
        return this.dto.optionBasic ? this.dto.optionBasic.optionType.toString() : undefined;
    }

    get optionIndicators(): IOptionIndicator {
        return {
            optionCount: this.dto.referencedOptionCount,
            dto: this.dto,
            selectedLanguages: this.languages
        };
    }

    get reference() {
        return this.dto.reference;
    }

    get referenceExc() {
        return this.dto.referenceExc;
    }

    get shorttext(): IMultiLineValue[] {
        return this.getText("shorttext", true);
    }

    get longtext(): IMultiLineValue[] {
        return this.getText("longtext", true);
    }

    get shortPCCDtext(): IMultiLineValue[] {
        return this.getOptionTextPCCDExports('shorttext', true);
    }

    get longPCCDtext(): IMultiLineValue[] {
        return this.getOptionTextPCCDExports('longtext', true);
    }

    get footer(): IMultiLineValue[] {
        return this.getText("footer");
    }

    get usp(): IMultiLineValue[] {
        return this.getText("usp");
    }

    get textExportStatus(): OptionDtoTextExportStatus | undefined {
        return this.dto.textExportStatus;
    }

    get textExportStatusMessage(): string | undefined {
        return this.dto.textExportStatusMessage;
    }

    get textExportDate(): Date | undefined {
        return this.dto.textExportDate;
    }

    get changedTimestamp(): number | undefined {
        return this.dto.metadata ? this.dto.metadata.changedTimestamp : undefined;
    }

    get changedBy(): string {
        return this.dto.metadata && this.dto.metadata.changedBy ? this.dto.metadata.changedBy : '';
    }

    // setter
    set textExportStatus(newValue: OptionDtoTextExportStatus | undefined) {
        this.dto.textExportStatus = newValue;
    }

    set textExportStatusMessage(newValue: string | undefined) {
        this.dto.textExportStatusMessage = newValue;
    }

    set textExportDate(newValue: Date | undefined) {
        this.dto.textExportDate = newValue;
    }

    set price(newValue: number | undefined) {
        if (newValue !== undefined) {
            this.dto.price = newValue;
        }
    }

    set cnr(newValue: CnrDto | undefined) {
        if (newValue) {
            this.dto.cnrId = newValue.objectId;
        } else {
            this.dto.cnrId = 0;
        }
    }

    set specificType(newValue: OptionDtoSpecificType | undefined) {
        this.dto.specificType = newValue;
    }

    set statusValueState(newValue: number | undefined) {
        if (newValue !== undefined) {
            // update column values
            this.optionStatus.statusView = valueState2str(newValue, this.workflows);
            this.optionStatus.statusValueState = newValue;

            // update dto property
            this.dto.status = getDtoStatus(newValue, this.dto.status, this.workflows);
        }
    }

    set texts(newValue: OptionTextDto[]) {
        const texts: OptionTextDto[] = newValue.filter((value: OptionTextDto) =>
            (value.objectId > 0 || (value.shorttext && value.shorttext.length > 0) || (value.longtext && value.longtext.length > 0)));

        texts.forEach((value: OptionTextDto) => {
            if (value.shorttext === "") {
                value.shorttext = undefined;
            }
            if (value.longtext === "") {
                value.longtext = undefined;
            }
        });
        this.dto.texts = texts;
    }

    set optionTextPCCDExports(newValue: OptionTextDto[]) {
        const optionTextPCCDExports: OptionTextDto[] = newValue.filter((value: OptionTextDto) =>
            (value.objectId > 0 || (value.shorttext && value.shorttext.length > 0) || (value.longtext && value.longtext.length > 0)));
        optionTextPCCDExports.forEach((value: OptionTextDto) => {
            if (value.shorttext === "") {
                value.shorttext = undefined;
            }
            if (value.longtext === "") {
                value.longtext = undefined;
            }
        });

        this.dto.optionTextPCCDExports = optionTextPCCDExports;
    }

    set availableEnd(newValue: string | undefined) {
        if (!newValue) {
            // the user deleted the property
            this.dto.availableEnd = undefined;
        } else {
            // don't overwrite the current date if the user entered
            // a malformed date string
            if (!isNaN(Date.parse(newValue))) {
                this.dto.availableEnd = newValue;
            }
        }
    }

    set availableStart(newValue: string | undefined) {
        if (!newValue) {
            // the user deleted the property
            this.dto.availableStart = undefined;
        } else {
            // don't overwrite the current date if the user entered
            // a malformed date string
            if (!isNaN(Date.parse(newValue))) {
                this.dto.availableStart = newValue;
            }
        }
    }

    set comment(newValue: string | undefined) {
        this.dto.comment = newValue;
    }

    set intSequence(newValue: number | undefined) {
        this.dto.intSequence = newValue === undefined ? 9999 : newValue;
    }

    set constraint(newValue: OptionBasicCoreDto[] | undefined) {
        this.dto.constraint = newValue;
    }

    set exclusion(newValue: OptionBasicCoreDto[] | undefined) {
        this.dto.exclusion = newValue;
    }

    set packageContent(newValue: OptionBasicCoreDto[] | undefined) {
        this.dto.packageContent = newValue;
    }

    set flags(newValue: FlagDto[] | undefined) {
        this.dto.flags = newValue;
    }

    set derivateStates(newValue: TDerivateStates) {
        const derivateStates: AssignedModeltypeBasicDto[] = [];

        _.forOwn(this.derivatesMap, (derName, key, object) => {
            if (key in newValue) {
                const dtoObjectId: number = Number(key);
                const assignmentType = DataAdapterOptionsView.getAssigment(newValue[key]);
                const mt = _.find(this.derivates, (dto: ModeltypeBasicDto) => dto.objectId === dtoObjectId);

                // Don't send assigned model type basics of type 'N'. If a model type
                // (derivate) assignement is set to 'N' (== not available), then remove
                // it from the OptionDto that get's sent to the back end.
                if (mt && assignmentType !== AssignedModeltypeBasicDtoAssignmentType.N) {
                    const derivateState: AssignedModeltypeBasicDto = {
                        assignmentType: assignmentType,
                        modeltypeBasic: mt,
                        objectId: 0,
                        option: {objectId: this.dto.objectId},
                        versionId: 0
                    };
                    const old = _.find(this.dto.modeltypes, (dto: AssignedModeltypeBasicDto) => {
                        return dto.modeltypeBasic !== undefined && dto.modeltypeBasic.objectId === mt.objectId;
                    });

                    // create new derivate states if changed and remove old states
                    if (old) {
                        old.assignmentType = derivateState.assignmentType;
                        old.modeltypeBasic = mt;
                        derivateStates.push(old);
                    }
                    else {
                        derivateStates.push(derivateState);
                    }
                }
            }
        });
        this.dto.modeltypes = derivateStates;
        this.updateDerivateStates(true);
    }

    set reference(newValue: string | undefined) {
        this.dto.reference = newValue;
    }

    set referenceExc(newValue: string | undefined) {
        this.dto.referenceExc = newValue;
    }

    set shorttext(newValue: IMultiLineValue[]) {
        this.setText('shorttext', newValue);
    }

    set longtext(newValue: IMultiLineValue[]) {
        this.setText('longtext', newValue);
    }

    set shortPCCDtext(newValue: IMultiLineValue[]) {
        this.setOptionTextPCCDExports('shorttext', newValue);
    }

    set longPCCDtext(newValue: IMultiLineValue[]) {
        this.setOptionTextPCCDExports('longtext', newValue);
    }

    set footer(newValue: IMultiLineValue[]) {
        this.setText('footer', newValue);
    }

    set usp(newValue: IMultiLineValue[]) {
        this.setText('usp', newValue);
    }

    // private
    private updateDerivateStates(force: boolean = false) {
        if (force || !this.state.derivateStates) {
            const {modeltypes = []} = this.dto;
            const derivateStates: TDerivateStates = {};
            _.forEach(modeltypes, (mt: AssignedModeltypeBasicDto) => {
                const derivate: string = this.derivatesMap[mt.modeltypeBasic.objectId];
                if (derivate) {
                    derivateStates[mt.modeltypeBasic.objectId] = DataAdapterOptionsView.getOptionState(mt.assignmentType);
                }
            });
            this.state.derivateStates = derivateStates;
        }
    }

    private getText(key: string, withState: boolean = false): IMultiLineValue[] {
        return this.languages.map(language => {
            const item: IMultiLineValue = {
                text: "",
                flag: lang2flag(language.vmce),
                caption: language.nameDe
            };

            // get text(based on key)
            let optionTextDto = _.find(this.dto.texts, (text: OptionTextDto) =>
                text.language && text.language.objectId === language.objectId && text[key] !== undefined);

            if (optionTextDto) {
                let optionText: string = optionTextDto[key];
                item.text = optionText;
            }

            // for option short and long text set also original text for compare
            if (withState) {
                let originalText;

                if (this.cnr && this.cnr.cnr === PropertiesKey.BasicMarketCnrKey) {
                    // for a Basic Market option, get the basic option text
                    originalText = this.dto.optionBasic.texts.find((text: OptionBasicTextDto) =>
                        text.languageDto !== undefined && text.languageDto.objectId === language.objectId
                        && text[key] !== undefined);
                } else if (this.dto.parentTexts) {
                    // for a specific option, get the parent Basic Market option text
                    originalText = this.dto.parentTexts.find((text: OptionTextDto) =>
                        text.language !== undefined && text.language.objectId === language.objectId
                        && text[key] !== undefined);
                }

                item.originalText = originalText ? originalText[key] : "";
            }

            return item;
        });
    }

    private setText(key: string, newValue: IMultiLineValue[]) {
        const lngtxts: IMultiLineValue[] = this.getText(key); // texts in the order and count like this.languages
        const chgtxts = _.map(lngtxts, (lngtxt: IMultiLineValue) => {
            const index = this.languages.findIndex(val => val.nameDe === lngtxt.caption);
            const language = this.languages[index];
            const chgtxt = _.find(newValue, (value: IMultiLineValue) =>
                lngtxt.flag === value.flag && lngtxt.text !== value.text);
            return chgtxt !== undefined ? {language, value: chgtxt} : undefined;
        });
        const {texts = []} = this.dto;
        _.forEach(chgtxts, (chgtxt) => {
            if (chgtxt === undefined)
                return;
            let text = _.find(texts, (textDto: OptionTextDto) => textDto.language !== undefined && chgtxt.language.objectId === textDto.language.objectId);
            if (text === undefined) {
                text = {objectId: 0, versionId: 0, language: {objectId: chgtxt.language.objectId}};
                if (this.dto.objectId > 0) {
                    text.option = {objectId: this.dto.objectId};
                }
                texts.push(text);
            }
            if (_.isString((chgtxt.value as any).value))
                text[key] = (chgtxt.value as any).value;
            else if (_.isString(chgtxt.value.text))
                text[key] = chgtxt.value.text;
            else
                text[key] = undefined;
        });
        this.dto.texts = texts;
    }

    private getOptionTextPCCDExports(key: string, withState: boolean = false): IMultiLineValue[] {
        return this.languages.map(language => {
            const item: IMultiLineValue = {
                text: "",
                flag: lang2flag(language.vmce),
                caption: language.nameDe
            };

            let optionTextDto = _.find(this.dto.optionTextPCCDExports, (text: OptionTextPCCDExportDto) =>
                text.languageDto && text.languageDto.objectId === language.objectId && text[key] !== undefined
            );

            if (optionTextDto) {
                let optionText: string = optionTextDto[key];
                item.text = optionText;
            }

            // for option short and long text set also original text for compare
            if (withState) {
                let originalText;

                if (this.cnr && this.cnr.cnr === PropertiesKey.BasicMarketCnrKey) {
                    // for a Basic Market option, get the basic option text
                    originalText = this.dto.optionBasic.optionBasicTextPCCDExports.find((text: OptionBasicTextDto) =>
                        text.languageDto !== undefined && text.languageDto.objectId === language.objectId
                        && text[key] !== undefined);
                } else if (this.dto.parentTexts) {
                    // for a specific option, get the parent Basic Market option text
                    originalText = this.dto.parentTexts.find((text: OptionTextDto) =>
                        text.language !== undefined && text.language.objectId === language.objectId
                        && text[key] !== undefined);
                }

                item.originalText = originalText ? originalText[key] : "";
            }

            return item;
        });
    }

    private setOptionTextPCCDExports(key: string, newValue: IMultiLineValue[]) {
        const lngtxts: IMultiLineValue[] = this.getOptionTextPCCDExports(key); // texts in the order and count like this.languages
        const chgtxts = _.map(lngtxts, (lngtxt: IMultiLineValue) => {
            const index = this.languages.findIndex(val => val.nameDe === lngtxt.caption);
            const language = this.languages[index];
            const chgtxt = _.find(newValue, (value: IMultiLineValue) =>
                lngtxt.flag === value.flag && lngtxt.text !== value.text);
            return chgtxt !== undefined ? {language, value: chgtxt} : undefined;
        });
        const {optionTextPCCDExports = []} = this.dto;

        _.forEach(chgtxts, (chgtxt) => {
            if (chgtxt === undefined)
                return;
            let text: any = _.find(optionTextPCCDExports, (textDto: OptionTextDto) => textDto.language !== undefined && chgtxt.language.objectId === textDto.language.objectId);
            if (text === undefined) {
                text = {objectId: 0, versionId: 0, language: {objectId: chgtxt.language.objectId}};
                if (this.dto.objectId > 0) {
                    text.option = {objectId: this.dto.objectId};
                }
                optionTextPCCDExports.push(text);
            }
            if (_.isString((chgtxt.value as any).value))
                text[key] = (chgtxt.value as any).value;
            else if (_.isString(chgtxt.value.text))
                text[key] = chgtxt.value.text;
            else
                text[key] = undefined;
        });
        this.dto.optionTextPCCDExports = optionTextPCCDExports;
    }

    getMissingPDiPAMTBs = () => {
        let missingAMTBs = new Set<string>();

        if (this.dto.modeltypes && this.dto.modeltypes.length > 0) {
            this.dto.modeltypes.forEach((amtb) => {
                if (amtb.missingPDiPAssignment === true) {
                    missingAMTBs.add(DataAdapterOptionsView.createDerivateName(amtb.modeltypeBasic));
                }
            });
        }

        return missingAMTBs;
    }
}



// WEBPACK FOOTER //
// ./src/views/OptionsView/DataAdapterOptionsView.tsx